import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useTranslation } from "react-i18next";
import { countActiveClientFilters, DEFAULT_CLIENT_FILTERS } from "../../hooks/useClientFilters";

const REGISTRATION_GROUPS = [
  {
    key: "type",
    options: [
      { value: "2", labelKey: "registrationOptions.pf" },
      { value: "3", labelKey: "registrationOptions.pj" },
    ],
  },
  {
    key: "contact",
    options: [
      { value: "8", labelKey: "registrationOptions.withEmail" },
      { value: "4", labelKey: "registrationOptions.withoutEmail" },
      { value: "10", labelKey: "registrationOptions.withMobile" },
      { value: "6", labelKey: "registrationOptions.withoutMobile" },
      { value: "9", labelKey: "registrationOptions.withPhone" },
      { value: "5", labelKey: "registrationOptions.withoutPhone" },
    ],
  },
  {
    key: "address",
    options: [
      { value: "11", labelKey: "registrationOptions.withAddress" },
      { value: "7", labelKey: "registrationOptions.withoutAddress" },
      { value: "1", labelKey: "registrationOptions.withLocation" },
    ],
  },
];

function ExclusiveChipGroup({ label, hint, options, value, onChange }) {
  const current = String(value || "");

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {options.map((opt) => {
          const active = current === String(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              clickable
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => onChange(active ? "" : String(opt.value))}
              sx={{ fontWeight: active ? 600 : 400 }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function ChipFilterGroup({ label, hint, options, value, onChange }) {
  const selected = useMemo(() => new Set((value || []).map(String)), [value]);

  const toggle = (v) => {
    const id = String(v);
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {label}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {options.map((opt) => {
          const active = selected.has(String(opt.value));
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              clickable
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => toggle(opt.value)}
              sx={{ fontWeight: active ? 600 : 400 }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function FilterAccordion({ panelId, title, subtitle, expanded, onChange, children, badge }) {
  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: "8px !important",
        "&:before": { display: "none" },
        "& + &": { mt: 1.5 },
        overflow: "hidden",
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 52 }}>
        <Box sx={{ flex: 1, pr: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            {badge > 0 ? (
              <Chip label={badge} size="small" color="primary" sx={{ height: 22, fontSize: 12 }} />
            ) : null}
          </Stack>
          {subtitle ? (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function ClientAdvancedFiltersDialog({
  open,
  filters,
  onPatch,
  onApply,
  onClear,
  onClose,
  draftActiveCount = 0,
}) {
  const { t } = useTranslation("settings");
  const f = { ...DEFAULT_CLIENT_FILTERS, ...filters };

  const [expanded, setExpanded] = useState({
    quality: true,
    profile: false,
    period: false,
    fields: false,
  });

  useEffect(() => {
    if (!open) return;
    setExpanded((prev) => ({
      ...prev,
      quality:
        Boolean(f.inconsistente) ||
        (f.opcoes_cadastro || []).length > 0 ||
        f.portal_acesso === "com" ||
        f.portal_acesso === "sem",
    }));
  }, [open, f.inconsistente, f.opcoes_cadastro, f.portal_acesso]);

  const sectionBadge = useMemo(
    () => ({
      quality:
        (f.inconsistente ? 1 : 0) +
        ((f.opcoes_cadastro || []).length > 0 ? 1 : 0) +
        (f.portal_acesso === "com" || f.portal_acesso === "sem" ? 1 : 0),
      profile:
        (f.potencial || []).length +
        (f.genero || []).length +
        (f.estado_civil || []).length,
      period:
        f.data_tipo !== "todo-periodo" && (f.data_inicio || f.data_fim) ? 1 : 0,
      fields: [
        "id_interno",
        "nome",
        "email",
        "celular",
        "telefone",
        "cpf_cnpj",
        "identidade",
        "profissao",
        "cargo",
      ].filter((k) => String(f[k] || "").trim()).length +
        (f.score_min !== "" && f.score_min != null ? 1 : 0) +
        (f.score_max !== "" && f.score_max != null ? 1 : 0),
    }),
    [f]
  );

  const potentialOpts = [
    { value: "1", label: t("clientFilters.potentialLow") },
    { value: "2", label: t("clientFilters.potentialMedium") },
    { value: "3", label: t("clientFilters.potentialHigh") },
  ];

  const genderOpts = [
    { value: "1", label: t("clientFilters.genderMale") },
    { value: "2", label: t("clientFilters.genderFemale") },
    { value: "3", label: t("clientFilters.genderOther") },
  ];

  const maritalOpts = [
    { value: "1", label: t("clientFilters.maritalSingle") },
    { value: "2", label: t("clientFilters.maritalMarried") },
    { value: "3", label: t("clientFilters.maritalDivorced") },
    { value: "4", label: t("clientFilters.maritalWidowed") },
    { value: "5", label: t("clientFilters.maritalSeparated") },
  ];

  const portalAccessOpts = [
    { value: "com", label: t("clientFilters.portalAccessWith") },
    { value: "sem", label: t("clientFilters.portalAccessWithout") },
  ];

  const toggleRegistration = (value) => {
    const id = String(value);
    const current = (f.opcoes_cadastro || []).map(String);
    const set = new Set(current);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onPatch({ opcoes_cadastro: [...set] });
  };

  const handleAccordion =
    (key) =>
    (_, isExpanded) => {
      setExpanded((prev) => ({ ...prev, [key]: isExpanded }));
    };

  const applyLabel =
    draftActiveCount > 0
      ? t("clientFilters.applyWithCount", { count: draftActiveCount })
      : t("clientFilters.apply");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              mt: 0.25,
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <TuneRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography variant="h6" component="div" fontWeight={700} lineHeight={1.3}>
              {t("clientFilters.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("clientFilters.subtitle")}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "action.hover" }}>
        <Stack spacing={2}>
          <Alert
            severity="warning"
            icon={<WarningAmberRoundedIcon fontSize="inherit" />}
            sx={{
              alignItems: "flex-start",
              borderRadius: 2,
              "& .MuiAlert-message": { width: "100%" },
            }}
            action={
              <Switch
                edge="end"
                checked={Boolean(f.inconsistente)}
                onChange={(e) => onPatch({ inconsistente: e.target.checked })}
                inputProps={{ "aria-label": t("clientFilters.inconsistentOnly") }}
              />
            }
          >
            <Typography variant="subtitle2" fontWeight={700}>
              {t("clientFilters.inconsistentOnly")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("clientFilters.inconsistentHint")}
            </Typography>
          </Alert>

          <FilterAccordion
            panelId="quality"
            title={t("clientFilters.sectionQuality")}
            subtitle={t("clientFilters.sectionQualityHint")}
            expanded={expanded.quality}
            onChange={handleAccordion("quality")}
            badge={sectionBadge.quality}
          >
            <Stack spacing={2.5}>
              {REGISTRATION_GROUPS.map((group) => (
                <Box key={group.key}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    textTransform="uppercase"
                    letterSpacing={0.6}
                    display="block"
                    sx={{ mb: 1 }}
                  >
                    {t(`clientFilters.registrationGroups.${group.key}`)}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {group.options.map((opt) => {
                      const active = (f.opcoes_cadastro || []).map(String).includes(String(opt.value));
                      return (
                        <Chip
                          key={opt.value}
                          label={t(`clientFilters.${opt.labelKey}`)}
                          size="small"
                          clickable
                          color={active ? "primary" : "default"}
                          variant={active ? "filled" : "outlined"}
                          onClick={() => toggleRegistration(opt.value)}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))}
              <ExclusiveChipGroup
                label={t("clientFilters.portalAccessTitle")}
                hint={t("clientFilters.portalAccessHint")}
                options={portalAccessOpts}
                value={f.portal_acesso}
                onChange={(v) => onPatch({ portal_acesso: v })}
              />
            </Stack>
          </FilterAccordion>

          <FilterAccordion
            panelId="profile"
            title={t("clientFilters.sectionProfile")}
            subtitle={t("clientFilters.sectionProfileHint")}
            expanded={expanded.profile}
            onChange={handleAccordion("profile")}
            badge={sectionBadge.profile}
          >
            <Stack spacing={2.5}>
              <ChipFilterGroup
                label={t("clientFilters.potential")}
                options={potentialOpts}
                value={f.potencial}
                onChange={(v) => onPatch({ potencial: v })}
              />
              <ChipFilterGroup
                label={t("clientFilters.gender")}
                options={genderOpts}
                value={f.genero}
                onChange={(v) => onPatch({ genero: v })}
              />
              <ChipFilterGroup
                label={t("clientFilters.maritalStatus")}
                options={maritalOpts}
                value={f.estado_civil}
                onChange={(v) => onPatch({ estado_civil: v })}
              />
            </Stack>
          </FilterAccordion>

          <FilterAccordion
            panelId="period"
            title={t("clientFilters.sectionPeriod")}
            subtitle={t("clientFilters.sectionPeriodHint")}
            expanded={expanded.period}
            onChange={handleAccordion("period")}
            badge={sectionBadge.period}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>{t("clientFilters.dateType")}</InputLabel>
                  <Select
                    label={t("clientFilters.dateType")}
                    value={f.data_tipo}
                    onChange={(e) => onPatch({ data_tipo: e.target.value })}
                  >
                    <MenuItem value="todo-periodo">{t("clientFilters.allPeriod")}</MenuItem>
                    <MenuItem value="entrada">{t("clientFilters.dateRegistration")}</MenuItem>
                    <MenuItem value="alteracao">{t("clientFilters.dateUpdated")}</MenuItem>
                    <MenuItem value="aniversariante">{t("clientFilters.dateBirthday")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("clientFilters.dateFrom")}
                  placeholder={t("clientFilters.datePlaceholder")}
                  value={f.data_inicio}
                  onChange={(e) => onPatch({ data_inicio: e.target.value })}
                  disabled={f.data_tipo === "todo-periodo"}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  fullWidth
                  label={t("clientFilters.dateTo")}
                  placeholder={t("clientFilters.datePlaceholder")}
                  value={f.data_fim}
                  onChange={(e) => onPatch({ data_fim: e.target.value })}
                  disabled={f.data_tipo === "todo-periodo"}
                />
              </Grid>
            </Grid>
          </FilterAccordion>

          <FilterAccordion
            panelId="fields"
            title={t("clientFilters.sectionFields")}
            subtitle={t("clientFilters.sectionFieldsHint")}
            expanded={expanded.fields}
            onChange={handleAccordion("fields")}
            badge={sectionBadge.fields}
          >
            <Grid container spacing={2}>
              {[
                ["id_interno", t("clientFilters.internalId"), t("clientFilters.internalIdHint")],
                ["nome", t("clientFilters.name"), t("clientFilters.nameHint")],
                ["email", t("shared.email"), t("clientFilters.emailHint")],
                ["celular", t("clientFilters.mobile"), t("clientFilters.phoneHint")],
                ["telefone", t("clientFilters.phone"), t("clientFilters.phoneHint")],
                ["cpf_cnpj", t("clientFilters.taxId"), t("clientFilters.taxIdHint")],
                ["identidade", t("clientFilters.identity"), null],
                ["profissao", t("clientFilters.profession"), null],
                ["cargo", t("clientFilters.jobTitle"), null],
              ].map(([key, label, hint]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    size="small"
                    fullWidth
                    label={label}
                    placeholder={hint || undefined}
                    value={f[key] || ""}
                    onChange={(e) => onPatch({ [key]: e.target.value })}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label={t("clientFilters.scoreMin")}
                  value={f.score_min}
                  onChange={(e) => onPatch({ score_min: e.target.value })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  fullWidth
                  type="number"
                  label={t("clientFilters.scoreMax")}
                  value={f.score_max}
                  onChange={(e) => onPatch({ score_max: e.target.value })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </FilterAccordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
        <Button
          onClick={onClear}
          color="inherit"
          disabled={draftActiveCount === 0 && !countActiveClientFilters(f)}
        >
          {t("clientFilters.clear")}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>{t("shared.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => {
              onApply?.();
              onClose?.();
            }}
          >
            {applyLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
